/**
 * "Variable mappings": one row per variable, `{{var}} maps to [path]`. The row
 * last focused is the one a click in the sample's JSON tree fills. With a
 * sample, each row also reads like Langfuse — `Input › messages › [0] › content`
 * — with the value it reads underneath.
 */

import { oneLine, valueAt } from "@/lib/json";
import type { PathInfo } from "./types";
import { PathInput } from "./path-input";
import { crumbs } from "./variables";

function Reads({ path, sample }: { path: string; sample: Record<string, unknown> }) {
  const value = valueAt(sample, path);
  const parts = crumbs(path);
  return (
    <span className="col-span-3 flex min-w-0 flex-col gap-0.5 pl-1">
      <span className="truncate font-mono text-faint" aria-label={`Reads ${parts.join(" › ")}`}>
        {parts.map((p, i) => (
          <span key={i}>
            {i > 0 && <span className="px-1">›</span>}
            {p}
          </span>
        ))}
      </span>
      <span
        className={`truncate ${value === undefined || value === null ? "text-faint italic" : "text-body"}`}
      >
        {value === undefined || value === null ? "No value in this sample" : oneLine(value, 120)}
      </span>
    </span>
  );
}

export interface MappingRow {
  name: string;
  label?: string;
  required: boolean;
}

export function MappingRows({
  rows,
  value,
  paths,
  sample,
  focused,
  onFocus,
  onChange,
  hint = "Click a field in the sample data to fill the selected row.",
}: {
  rows: MappingRow[];
  value: Record<string, string>;
  paths: PathInfo[];
  sample?: Record<string, unknown> | undefined;
  focused: string | null;
  onFocus: (name: string) => void;
  onChange: (name: string, path: string) => void;
  hint?: string;
}) {
  if (rows.length === 0)
    return (
      <p className="text-xs text-muted">No variables yet. Add one to the prompt, like {"{{output}}"}.</p>
    );
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => {
        const label = `{{${r.name}}} maps to`;
        return (
          <div
            key={r.name}
            className={`grid grid-cols-[minmax(0,170px)_auto_minmax(0,1fr)] items-center gap-2 rounded-md px-2 py-1.5 text-xs ${focused === r.name ? "bg-selected" : ""}`}
          >
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-mono font-semibold">{`{{${r.name}}}`}</span>
              {(r.label || !r.required) && (
                <span className="truncate text-faint">
                  {r.label}
                  {!r.required && `${r.label ? " · " : ""}optional`}
                </span>
              )}
            </span>
            <span className="text-faint">maps to</span>
            <PathInput
              label={label}
              value={value[r.name] ?? ""}
              paths={paths}
              sample={sample}
              invalid={r.required && !value[r.name]?.trim()}
              onFocus={() => onFocus(r.name)}
              onChange={(path) => onChange(r.name, path)}
            />
            {sample && value[r.name]?.trim() && <Reads path={value[r.name]!.trim()} sample={sample} />}
          </div>
        );
      })}
      <span className="text-xs text-faint">{hint}</span>
    </div>
  );
}
